import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import CTA from "@/components/CTA";
import Seo from "@/components/Seo";
import {
  Sun, WifiHigh, VideoCamera, Truck, ShieldCheck, Clock,
  MapPin, Buildings, Wrench, Lightning, Warning, Car, Barricade,
  BuildingApartment, House, Confetti, CaretRight, Check
} from "@phosphor-icons/react";

const HERO_IMG = "https://images.pexels.com/photos/9799718/pexels-photo-9799718.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const INDUSTRY_ICON = {
  "construction-site-security": Wrench,
  "commercial-property-security": Buildings,
  "auto-dealership-security": Car,
  "equipment-yard-security": Barricade,
  "utility-energy-security": Lightning,
  "government-municipal-security": BuildingApartment,
  "parking-lot-security": Car,
  "vacant-property-security": House,
  "special-event-security": Confetti,
  "critical-infrastructure-security": Warning,
};

export default function Home() {
  const [systems, setSystems] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    api.get("/systems").then((r) => setSystems(r.data)).catch(() => {});
    api.get("/industries").then((r) => setIndustries(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data.slice(0, 6))).catch(() => {});
    api.get("/case_studies").then((r) => setCases(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Solar Surveillance Trailers for Nevada & Utah"
        description="Rapid-deployment solar surveillance trailers and pole-mounted systems for construction, commercial, government and remote sites in Nevada and Utah. No permanent power. Cellular connected. Remote viewing."
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Solar surveillance trailer on active site" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/50 to-obsidian" />
        </div>
        <div className="relative container-x pt-16 pb-24 md:pt-24 md:pb-32">
          <div className="max-w-4xl">
            <div className="eyebrow text-solar mb-5 flex items-center gap-2">
              <span className="inline-block w-8 h-px bg-solar" /> NEVADA · UTAH · RAPID DEPLOYMENT
            </div>
            <h1 className="text-white mb-6" style={{textShadow: "0 2px 12px rgba(0,0,0,0.5)"}}>
              Solar Surveillance <br className="hidden md:block" />
              <span className="text-solar">Trailers</span> for Sites <br className="hidden md:block" />
              That Can't Go Dark.
            </h1>
            <p className="text-white/85 text-lg md:text-xl max-w-2xl mb-8 leading-relaxed">
              Protect construction sites, equipment, commercial properties and remote locations with rapidly deployable solar-powered surveillance — no permanent power, no wired internet.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link data-testid="hero-quote-btn" to="/request-a-quote" className="btn-solar text-center">Request a Security Assessment</Link>
              <Link data-testid="hero-compare-btn" to="/systems/compare" className="btn-ghost text-center">Compare Our Systems</Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
              <span className="flex items-center gap-2"><Check size={16} weight="bold" className="text-solar" /> Short-term & long-term</span>
              <span className="flex items-center gap-2"><Check size={16} weight="bold" className="text-solar" /> Self or monitored options</span>
              <span className="flex items-center gap-2"><Check size={16} weight="bold" className="text-solar" /> 24–72 hr deployment*</span>
            </div>
            <div className="text-[11px] text-white/40 mt-2">*Subject to unit availability and site location.</div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section data-testid="section-trust-bar" className="border-y border-chrome bg-steel">
        <div className="container-x py-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {[
            { icon: ShieldCheck, label: "NDOT SBE Certified" },
            { icon: ShieldCheck, label: "Nevada CCTV Approved" },
            { icon: Sun, label: "Solar-Powered" },
            { icon: WifiHigh, label: "Cellular Connected" },
            { icon: VideoCamera, label: "Remote Viewing" },
            { icon: Clock, label: "Rapid Deployment" },
          ].map((t) => (
            <div key={t.label} className="flex items-center gap-3 border-l-2 border-solar pl-4">
              <t.icon size={22} weight="fill" className="text-solar shrink-0" />
              <span className="text-xs uppercase tracking-wider font-semibold text-white/85">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* PROBLEM / SOLUTION */}
      <section className="container-x py-20 md:py-28">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
          <div>
            <div className="eyebrow text-solar mb-4">The Problem</div>
            <h2 className="mb-6">Security where permanent infrastructure isn't practical.</h2>
            <p className="text-white/75 text-lg leading-relaxed">
              Job sites move. Utilities aren't in place. Wired internet isn't available. Guard-only coverage isn't consistent — and copper, tools, equipment and vehicles keep disappearing.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-px bg-chrome border border-chrome">
            {[
              "Jobsite theft",
              "Equipment loss",
              "Vandalism",
              "Trespassing",
              "Blind spots",
              "Remote locations",
              "Temporary projects",
              "Sites without power or internet",
            ].map((p) => (
              <div key={p} className="bg-steel p-6 flex items-center gap-3">
                <div className="w-2 h-2 bg-solar" />
                <span className="font-semibold text-white/90 text-sm">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SYSTEM COMPARISON */}
      <section data-testid="section-systems" className="bg-steel py-20 md:py-28 border-y border-chrome">
        <div className="container-x">
          <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="eyebrow text-solar mb-3">Systems</div>
              <h2>Three configurations. One deployment model.</h2>
            </div>
            <Link to="/systems/compare" className="text-solar font-bold uppercase tracking-wider text-sm flex items-center gap-2">
              Full Comparison <CaretRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-chrome border border-chrome">
            {systems.map((s, i) => (
              <SystemCard key={s.slug} sys={s} highlighted={i === 1} />
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-x py-20 md:py-28">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16">
          <div>
            <div className="eyebrow text-solar mb-3">How it works</div>
            <h2 className="mb-6">Four steps from site to signal.</h2>
            <Link to="/request-a-quote" data-testid="how-it-works-cta" className="btn-solar inline-block">Start Your Site Assessment</Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-chrome border border-chrome">
            {[
              { n: "01", t: "Tell us about the site", d: "Share the location, layout and the security concern." },
              { n: "02", t: "We evaluate and recommend", d: "Site assessment determines the right configuration and unit count." },
              { n: "03", t: "Delivered and activated", d: "Trailer or pole system is positioned and brought online." },
              { n: "04", t: "Live and recorded access", d: "Authorized users get monitoring and footage from any device." },
            ].map((s) => (
              <div key={s.n} className="bg-obsidian p-8">
                <div className="font-display text-solar text-4xl font-black mb-3">{s.n}</div>
                <div className="font-display uppercase text-xl font-bold mb-2 leading-tight">{s.t}</div>
                <div className="text-white/70 text-sm leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-steel py-20 md:py-28 border-y border-chrome">
        <div className="container-x">
          <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="eyebrow text-solar mb-3">Industries</div>
              <h2>Built for the sites that need it most.</h2>
            </div>
            <Link to="/industries" className="text-solar font-bold uppercase tracking-wider text-sm flex items-center gap-2">
              All Industries <CaretRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-chrome border border-chrome">
            {industries.slice(0, 8).map((ind) => {
              const Icon = INDUSTRY_ICON[ind.slug] || Buildings;
              return (
                <Link
                  key={ind.slug}
                  to={`/industries/${ind.slug}`}
                  data-testid={`industry-card-${ind.slug}`}
                  className="group bg-obsidian p-6 hover:bg-steel transition-colors"
                >
                  <Icon size={30} weight="duotone" className="text-solar mb-4" />
                  <div className="font-display uppercase text-lg font-bold mb-2 leading-tight group-hover:text-solar transition-colors">{ind.short || ind.title}</div>
                  <div className="text-white/60 text-sm leading-relaxed">{ind.summary?.substring(0, 96)}…</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* KEY ADVANTAGES */}
      <section className="container-x py-20 md:py-28">
        <div className="eyebrow text-solar mb-3">Advantages</div>
        <h2 className="mb-12 max-w-3xl">Everything a fixed camera can't do.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-chrome border border-chrome">
          {[
            ["No permanent electrical connection", Sun],
            ["No wired internet required", WifiHigh],
            ["Rapid deployment", Truck],
            ["Relocatable coverage", MapPin],
            ["Remote live & recorded access", VideoCamera],
            ["Visible deterrence", Warning],
            ["Flexible rental periods", Clock],
            ["Configurable storage", VideoCamera],
            ["Scalable multi-unit deployment", Buildings],
          ].map(([label, Icon]) => (
            <div key={label} className="bg-obsidian p-6 flex items-start gap-3">
              <Icon size={22} weight="fill" className="text-solar shrink-0 mt-1" />
              <span className="font-semibold text-white/90">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CASE STUDIES */}
      {cases.length > 0 && (
        <section className="bg-steel py-20 md:py-28 border-y border-chrome">
          <div className="container-x">
            <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <div className="eyebrow text-solar mb-3">Recent Projects</div>
                <h2>How sites use 360 View.</h2>
              </div>
              <Link to="/case-studies" className="text-solar font-bold uppercase tracking-wider text-sm flex items-center gap-2">
                All Case Studies <CaretRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {cases.slice(0, 2).map((c) => (
                <Link key={c.slug} to={`/case-studies/${c.slug}`} data-testid={`case-card-${c.slug}`}
                  className="group border border-chrome bg-obsidian hover:border-solar transition-colors block">
                  <div className="aspect-[16/9] overflow-hidden bg-steel">
                    <img src={c.image} alt={c.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-6">
                    <div className="eyebrow text-solar mb-2">{c.city}, {c.state} · {c.system_name}</div>
                    <div className="font-display uppercase text-xl font-bold mb-3 group-hover:text-solar transition-colors">{c.title}</div>
                    <div className="text-white/70 text-sm leading-relaxed">{c.problem}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Preview */}
      <section className="container-x py-20 md:py-28">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16">
          <div>
            <div className="eyebrow text-solar mb-3">Common Questions</div>
            <h2 className="mb-6">Answers before you ask.</h2>
            <Link to="/faq" className="btn-solar inline-block">All FAQs</Link>
          </div>
          <div className="divide-y divide-chrome border-t border-b border-chrome">
            {faqs.map((f) => (
              <details key={f.slug} className="group py-5">
                <summary data-testid={`faq-${f.slug}`} className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <span className="font-semibold text-white text-base md:text-lg">{f.question}</span>
                  <CaretRight size={18} weight="bold" className="shrink-0 text-solar mt-1 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="mt-3 text-white/75 text-sm leading-relaxed">{f.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
}

function SystemCard({ sys, highlighted }) {
  return (
    <div className={`bg-obsidian p-8 flex flex-col ${highlighted ? "ring-1 ring-solar relative" : ""}`}>
      {highlighted && (
        <div className="absolute -top-3 left-8 bg-solar text-black text-[10px] font-black uppercase tracking-widest px-3 py-1">
          Most Popular
        </div>
      )}
      <div className="eyebrow text-solar mb-2">{sys.name}</div>
      <div className="font-display uppercase text-2xl font-bold leading-tight mb-3">{sys.short_name}</div>
      <p className="text-white/70 text-sm mb-6 flex-grow">{sys.summary?.substring(0, 160)}…</p>
      <ul className="space-y-2 mb-6">
        {(sys.features || []).slice(0, 4).map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-white/80">
            <Check size={16} weight="bold" className="text-solar mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link to={`/systems/${sys.slug}`} data-testid={`system-cta-${sys.slug}`} className="btn-ghost text-center mt-auto">
        View Details
      </Link>
    </div>
  );
}
